
import { useState, useEffect } from 'react';
import { supabase, uploadImage } from '../../utils/supabase';
import ContactFooter from '../../components/feature/ContactFooter';

interface LostPetReport {
  id: string;
  reporter_name?: string;
  reporter_phone?: string;
  pet_description: string;
  location_description: string;
  image_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  category: 'perdido' | 'abandonado' | 'herido';
  admin_notes?: string;
  resolved: boolean;
  created_at: string;
  updated_at: string;
}

export default function HuellitasPerdidas() {
  const [reports, setReports] = useState<LostPetReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReportForm, setShowReportForm] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'perdido' | 'abandonado' | 'herido'>('perdido');
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    loadApprovedReports();
  }, []);

  const loadApprovedReports = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('lost_pets_reports')
        .select('*')
        .eq('status', 'approved')
        .eq('resolved', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error cargando reportes:', error);
      } else {
        setReports(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona solo archivos de imagen válidos');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const uploadedUrl = await uploadImage(file, 'lost-pets');
      if (uploadedUrl) {
        setImagePreview(uploadedUrl);
      } else {
        alert('Error al subir la imagen');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmitReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imagePreview) {
      alert('La foto es obligatoria para reportar un caso');
      return;
    }

    setSubmittingReport(true);
    const formData = new FormData(e.target as HTMLFormElement);

    try {
      const reportData = {
        reporter_name: formData.get('reporter_name') as string || null,
        reporter_phone: formData.get('reporter_phone') as string || null,
        pet_description: formData.get('pet_description') as string,
        location_description: formData.get('location_description') as string,
        image_url: imagePreview,
        category: selectedCategory,
        status: 'pending',
        resolved: false,
      };

      const { error } = await supabase
        .from('lost_pets_reports')
        .insert([reportData]);

      if (error) {
        throw error;
      }

      alert('¡Reporte enviado exitosamente! Será revisado por nuestro equipo antes de publicarse.');
      setShowReportForm(false);
      setImagePreview('');
      setSelectedCategory('perdido');
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('Error enviando reporte:', error);
      alert('Error al enviar el reporte. Por favor intenta de nuevo.');
    } finally {
      setSubmittingReport(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'perdido':
        return 'ri-search-line';
      case 'abandonado':
        return 'ri-alarm-warning-line';
      case 'herido':
        return 'ri-heart-pulse-line';
      default:
        return 'ri-heart-line';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'perdido':
        return 'bg-blue-100 text-blue-800';
      case 'abandonado':
        return 'bg-red-100 text-red-800';
      case 'herido':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'perdido':
        return 'Perdido';
      case 'abandonado':
        return 'Abandonado';
      case 'herido':
        return 'Herido';
      default:
        return 'Desconocido';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver</span>
            </button>
            
            <div 
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => window.REACT_APP_NAVIGATE('/')}
            >
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-sm text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>

            <button
              onClick={() => setShowReportForm(true)}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer flex items-center space-x-2"
            >
              <i className="ri-add-line"></i>
              <span>Reportar Caso</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-500 to-orange-500 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              🐾 Huellitas en Alerta
            </h2>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Cada reporte cuenta. Ayúdanos a rescatar, cuidar y encontrar hogares para perritos que necesitan nuestra ayuda.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowReportForm(true)}
                className="bg-white text-red-500 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
              >
                <i className="ri-camera-line mr-2"></i>
                Reportar un Caso
              </button>
              <button
                onClick={() => {
                  const alertSection = document.querySelector('#huellitas-alerta');
                  if (alertSection) {
                    alertSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-white/20 hover:bg-white/30 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm cursor-pointer"
              >
                <i className="ri-eye-line mr-2"></i>
                Ver Casos Activos
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {reports.filter(r => r.category === 'perdido').length}
              </h3>
              <p className="text-gray-600">Perritos Perdidos</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-alarm-warning-line text-2xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {reports.filter(r => r.category === 'abandonado').length}
              </h3>
              <p className="text-gray-600">Casos de Abandono</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {reports.filter(r => r.category === 'herido').length}
              </h3>
              <p className="text-gray-600">Perritos Heridos</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - Huellitas en Alerta */}
      <main id="huellitas-alerta" className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">🚨 Huellitas en Alerta</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Estos casos han sido reportados por la comunidad y necesitan nuestra atención inmediata
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-red-500 animate-spin mb-4"></i>
              <p className="text-gray-600">Cargando casos activos...</p>
            </div>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg mb-4">No hay casos activos en este momento</p>
            <p className="text-gray-400">¡Eso es una buena noticia! Pero mantente alerta por si aparece algún caso.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {/* Imagen */}
                <div className="relative">
                  <img
                    src={report.image_url || 'https://readdy.ai/api/search-image?query=Lost%20or%20abandoned%20dog%20needing%20help%2C%20sad%20rescue%20dog%20looking%20for%20home%2C%20heartwarming%20animal%20rescue%20scene&width=400&height=300&seq=lost-pet-default&orientation=landscape'}
                    alt="Perrito reportado"
                    className="w-full h-48 object-cover object-top"
                  />
                  
                  {/* Etiqueta de categoría */}
                  <div className={`absolute top-2 left-2 px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(report.category)}`}>
                    <i className={`${getCategoryIcon(report.category)} mr-1`}></i>
                    {getCategoryLabel(report.category)}
                  </div>

                  {/* Fecha del reporte */}
                  <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                    {new Date(report.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short'
                    })}
                  </div>
                </div>

                <div className="p-6">
                  {/* Ubicación */}
                  <div className="flex items-center text-gray-600 mb-3">
                    <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                    <span className="font-medium">{report.location_description}</span>
                  </div>

                  {/* Descripción */}
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed line-clamp-3">
                    {report.pet_description}
                  </p>

                  {/* Información de contacto */}
                  {report.reporter_phone && (
                    <div className="flex items-center text-sm text-gray-600 mb-4">
                      <i className="ri-phone-fill mr-2 text-green-500"></i>
                      <span>Contacto disponible</span>
                    </div>
                  )}

                  {/* Estado del reporte */}
                  <div className="flex justify-between items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                      <i className="ri-check-line mr-1"></i>
                      Caso Activo
                    </span>
                    
                    {report.reporter_phone && (
                      <button
                        onClick={() => {
                          const message = `Hola, vi tu reporte sobre el perrito ${report.category} en ${report.location_description}. ¿Puedes darme más información?`;
                          const cleanNumber = report.reporter_phone?.replace(/[^0-9]/g, '') || '';
                          const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
                          window.open(whatsappUrl, '_blank');
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-whatsapp-fill mr-1"></i>
                        Contactar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Report Form Modal */}
      {showReportForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-camera-line text-2xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Reportar un Caso</h3>
                <p className="text-gray-600">Ayúdanos a ayudar. Tu reporte será revisado antes de publicarse.</p>
              </div>

              <form onSubmit={handleSubmitReport} className="space-y-4">
                {/* Foto obligatoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto del perrito *
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required
                    disabled={uploadingImage}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La foto es obligatoria. Máximo 5MB.
                  </p>

                  {uploadingImage && (
                    <div className="mt-2 text-sm text-gray-500 flex items-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Subiendo imagen...
                    </div>
                  )}

                  {imagePreview && (
                    <div className="mt-3">
                      <img
                        src={imagePreview}
                        alt="Vista previa"
                        className="w-full h-32 object-cover rounded-md border"
                      />
                    </div>
                  )}
                </div>

                {/* Categoría */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de caso *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('perdido')}
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        selectedCategory === 'perdido'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <i className="ri-search-line text-lg mb-1"></i>
                      <div className="text-xs font-medium">Perdido</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('abandonado')}
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        selectedCategory === 'abandonado'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <i className="ri-alarm-warning-line text-lg mb-1"></i>
                      <div className="text-xs font-medium">Abandonado</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('herido')}
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        selectedCategory === 'herido'
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <i className="ri-heart-pulse-line text-lg mb-1"></i>
                      <div className="text-xs font-medium">Herido</div>
                    </button>
                  </div>
                </div>

                {/* Ubicación */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicación aproximada *
                  </label>
                  <input
                    name="location_description"
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Ej: Calle 5 de Mayo, Colonia Centro, cerca del parque"
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción del caso *
                  </label>
                  <textarea
                    name="pet_description"
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Describe la situación: ¿cómo está el perrito? ¿qué necesita? ¿desde cuándo lo has visto?"
                  ></textarea>
                </div>

                {/* Nombre del reportero (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tu nombre (opcional)
                  </label>
                  <input
                    name="reporter_name"
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Tu nombre"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tu nombre será público en el reporte
                  </p>
                </div>

                {/* Teléfono de contacto (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de contacto (opcional)
                  </label>
                  <input
                    name="reporter_phone"
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Tu número de WhatsApp"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tu teléfono se hará público para que te contacten
                  </p>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReportForm(false);
                      setImagePreview('');
                      setSelectedCategory('perdido');
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submittingReport || uploadingImage || !imagePreview}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer disabled:opacity-50"
                  >
                    {submittingReport ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill mr-2"></i>
                        Enviar Reporte
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Contact Footer */}
      <ContactFooter />
    </div>
  );
}
